import { useMemo, CSSProperties } from 'react';

export type FloatingPosition = 'top' | 'bottom' | 'left' | 'right';
export type FloatingAlignment = 'start' | 'center' | 'end' | 'between';

export interface FloatingOffset {
  x?: number;
  y?: number;
}

export interface UseFloatingPositionOptions {
  position?: FloatingPosition;
  alignment?: FloatingAlignment;
  offset?: FloatingOffset;
}

export interface FloatingPositionResult {
  positionClasses: string[];
  positionStyles: CSSProperties;
}

/**
 * Shared positioning logic for floating elements (dropdowns, tooltips, popovers, etc.)
 * Returns CSS classes and inline styles for positioning
 */
export const useFloatingPosition = ({
  position = 'bottom',
  alignment = 'end',
  offset = { x: 0, y: 0 },
}: UseFloatingPositionOptions): FloatingPositionResult => {
  const positionClasses = useMemo(() => {
    return [`floating--${position}`, `floating--align-${alignment}`];
  }, [position, alignment]);

  const positionStyles = useMemo((): CSSProperties => {
    const style: CSSProperties = {};

    // Apply offset transformations
    if (offset.x || offset.y) {
      const transforms: string[] = [];

      if (offset.x) {
        transforms.push(`translateX(${offset.x}px)`);
      }
      if (offset.y) {
        transforms.push(`translateY(${offset.y}px)`);
      }

      if (transforms.length > 0) {
        style.transform = transforms.join(' ');
      }
    }

    return style;
  }, [offset.x, offset.y]);

  return {
    positionClasses,
    positionStyles,
  };
};

/**
 * Generate position-specific CSS class names with a custom prefix
 */
export const getFloatingPositionClasses = (
  prefix: string,
  position: FloatingPosition,
  alignment: FloatingAlignment
): string[] => {
  return [`${prefix}--${position}`, `${prefix}--align-${alignment}`];
};

/**
 * Calculate offset styles for floating elements
 */
export const getFloatingOffsetStyles = (offset: FloatingOffset): React.CSSProperties => {
  const style: React.CSSProperties = {};

  if (offset.x || offset.y) {
    const transforms: string[] = [];

    if (offset.x) {
      transforms.push(`translateX(${offset.x}px)`);
    }
    if (offset.y) {
      transforms.push(`translateY(${offset.y}px)`);
    }

    if (transforms.length > 0) {
      style.transform = transforms.join(' ');
    }
  }

  return style;
};
